import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedLeadForMobile, setSelectedLeadForMobile] = useState(null);

  // Check if already logged in via localStorage
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
      fetchLeads();
    }
  }, []);

  const handleLoginChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, credentials);
      if (res.data.success) {
        localStorage.setItem('adminToken', res.data.token);
        setIsAuthenticated(true);
        setLoginError('');
        fetchLeads();
      }
    } catch (err) {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setLeads([]);
    setMobileMenuOpen(false);
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/leads`);
      setLeads(res.data.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
    setIsLoading(false);
  };

  const openLeadDetails = async (lead) => {
    setSelectedLead(lead);
    setSelectedLeadForMobile(lead);
    
    // If it's a new lead, update it to 'Seen' in the DB and locally
    if (lead.status === 'New') {
      try {
        await axios.patch(`${API_URL}/api/leads/${lead._id}/status`);
        // Update local state to remove the blue dot instantly
        setLeads(leads.map(l => l._id === lead._id ? { ...l, status: 'Seen' } : l));
      } catch (error) {
        console.error('Error updating lead status:', error);
      }
    }
  };

  const closeLeadDetails = () => {
    setSelectedLead(null);
    setSelectedLeadForMobile(null);
  };

  // Mobile Lead Card Component
  const MobileLeadCard = ({ lead }) => (
    <div 
      className={`bg-white p-4 rounded-xl shadow-sm border ${lead.status === 'New' ? 'border-blue-300 bg-blue-50/20' : 'border-gray-100'} mb-3`}
      onClick={() => openLeadDetails(lead)}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {lead.status === 'New' && (
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            )}
            <h3 className="font-bold text-gray-800">{lead.firstName} {lead.lastName}</h3>
          </div>
          <p className="text-sm text-gray-600">{lead.streetAddress}</p>
          <p className="text-xs text-gray-500">{lead.city}, {lead.state}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-green-600">${lead.askingPrice}</p>
          <p className="text-xs text-gray-500">{new Date(lead.createdAt).toLocaleDateString()}</p>
        </div>
      </div>
      
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>{lead.email}</span>
        </div>
        <button className="text-propwhiz-accent text-sm font-medium">
          View Details →
        </button>
      </div>
    </div>
  );

  // --- LOGIN SCREEN (already responsive) ---
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-propwhiz-blue mb-2">Admin Login</h1>
            <p className="text-sm sm:text-base text-gray-500">Enter your credentials to access the dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input 
                type="text" 
                name="username" 
                value={credentials.username} 
                onChange={handleLoginChange} 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-propwhiz-accent text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input 
                type="password" 
                name="password" 
                value={credentials.password} 
                onChange={handleLoginChange} 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-propwhiz-accent text-sm sm:text-base"
              />
            </div>
            {loginError && <p className="text-red-500 text-sm text-center">{loginError}</p>}
            <button type="submit" className="w-full bg-propwhiz-blue text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition text-sm sm:text-base">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- DASHBOARD SCREEN (fully responsive) ---
  return (
    <div className="min-h-screen bg-gray-100 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm mb-4 sm:mb-6 lg:mb-8">
          <div className="w-full sm:w-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">Leads Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-500">Manage your property cash offers</p>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="sm:hidden w-full">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg border border-gray-200"
            >
              <span className="font-medium text-gray-700">Menu</span>
              <svg className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Mobile Dropdown Menu */}
            {mobileMenuOpen && (
              <div className="absolute right-4 left-4 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-2 z-10">
                <button 
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-red-500 hover:bg-red-50 rounded-lg font-semibold transition flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* Desktop Logout Button */}
          <button 
            onClick={handleLogout} 
            className="hidden sm:block px-4 sm:px-6 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 font-semibold transition text-sm sm:text-base"
          >
            Logout
          </button>
        </div>

        {/* Leads Section - Mobile Cards / Desktop Table */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="flex justify-center items-center gap-2">
                <svg className="animate-spin h-5 w-5 text-propwhiz-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading leads...</span>
              </div>
            </div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-lg font-medium">No leads found yet.</p>
              <p className="text-sm">When sellers submit the form, they'll appear here.</p>
            </div>
          ) : (
            <>
              {/* Mobile View - Card Layout */}
              <div className="block sm:hidden p-4">
                {leads.map((lead) => (
                  <MobileLeadCard key={lead._id} lead={lead} />
                ))}
              </div>

              {/* Desktop View - Table Layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px] lg:min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-gray-600 text-xs lg:text-sm uppercase tracking-wider">
                      <th className="p-3 lg:p-4 font-medium w-12"></th>
                      <th className="p-3 lg:p-4 font-medium">Name</th>
                      <th className="p-3 lg:p-4 font-medium">Property Details</th>
                      <th className="p-3 lg:p-4 font-medium">Timeline</th>
                      <th className="p-3 lg:p-4 font-medium">Asking Price</th>
                      <th className="p-3 lg:p-4 font-medium">Date</th>
                      <th className="p-3 lg:p-4 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leads.map((lead) => (
                      <tr 
                        key={lead._id} 
                        className={`hover:bg-gray-50 transition ${lead.status === 'New' ? 'bg-blue-50/30' : ''}`}
                      >
                        <td className="p-3 lg:p-4 text-center">
                          {/* BLUE DOT FOR NEW LEADS */}
                          {lead.status === 'New' && (
                            <div className="w-2 h-2 lg:w-3 lg:h-3 bg-blue-500 rounded-full mx-auto shadow-sm"></div>
                          )}
                        </td>
                        <td className="p-3 lg:p-4">
                          <div className="font-bold text-gray-800 text-sm lg:text-base">{lead.firstName} {lead.lastName}</div>
                          <div className="text-xs lg:text-sm text-gray-500 break-all">{lead.email}</div>
                          <div className="text-xs lg:text-sm text-gray-500">{lead.phone}</div>
                        </td>
                        <td className="p-3 lg:p-4">
                          <div className="text-gray-800 font-medium text-sm lg:text-base">{lead.streetAddress}</div>
                          <div className="text-xs lg:text-sm text-gray-500">{lead.city}, {lead.state}</div>
                        </td>
                        <td className="p-3 lg:p-4 text-gray-700 text-sm lg:text-base">{lead.sellTimeline}</td>
                        <td className="p-3 lg:p-4 font-bold text-green-600 text-sm lg:text-base">${lead.askingPrice}</td>
                        <td className="p-3 lg:p-4 text-xs lg:text-sm text-gray-500">
                          {new Date(lead.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3 lg:p-4">
                          <button 
                            onClick={() => openLeadDetails(lead)}
                            className="text-propwhiz-accent font-semibold hover:text-cyan-700 text-xs lg:text-sm whitespace-nowrap"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Summary Cards - Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
            <p className="text-xs sm:text-sm text-gray-500">Total Leads</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">{leads.length}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
            <p className="text-xs sm:text-sm text-gray-500">New</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600">{leads.filter(l => l.status === 'New').length}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
            <p className="text-xs sm:text-sm text-gray-500">Avg. Price</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">
              ${leads.length ? Math.round(leads.reduce((acc, l) => acc + (l.askingPrice || 0), 0) / leads.length).toLocaleString() : 0}
            </p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm col-span-2 sm:col-span-1">
            <p className="text-xs sm:text-sm text-gray-500">This Month</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-propwhiz-blue">
              {leads.filter(l => new Date(l.createdAt).getMonth() === new Date().getMonth()).length}
            </p>
          </div>
        </div>
      </div>

      {/* LEAD DETAILS MODAL - Fully Responsive */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden relative animate-slide-up sm:animate-none">
            
            {/* Modal Header */}
            <div className="bg-gray-50 p-4 sm:p-6 border-b flex justify-between items-center sticky top-0 z-10">
              <div className="flex items-center gap-2">
                {selectedLead.status === 'New' && (
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></div>
                )}
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">Lead Details</h2>
              </div>
              <button 
                onClick={closeLeadDetails} 
                className="text-gray-400 hover:text-gray-800 transition p-2 hover:bg-gray-200 rounded-full"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(80vh-100px)]">
              <div className="space-y-4 sm:space-y-6">
                
                {/* Contact Info */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  <h3 className="text-xs sm:text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-semibold text-sm sm:text-base break-words">{selectedLead.firstName} {selectedLead.lastName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-semibold text-sm sm:text-base break-all">{selectedLead.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-semibold text-sm sm:text-base">{selectedLead.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Submitted On</p>
                      <p className="font-semibold text-sm sm:text-base">{new Date(selectedLead.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div className="bg-blue-50 p-3 sm:p-4 rounded-xl border border-blue-100">
                  <h3 className="text-xs sm:text-sm font-bold text-blue-400 uppercase tracking-wider mb-3">Property Status</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs text-blue-600/70">Timeline to Sell</p>
                      <p className="font-semibold text-blue-900 text-sm sm:text-base">{selectedLead.sellTimeline}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600/70">Property Condition</p>
                      <p className="font-semibold text-blue-900 text-sm sm:text-base">{selectedLead.propertyCondition}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600/70">Listed with Realtor?</p>
                      <p className="font-semibold text-blue-900 text-sm sm:text-base">{selectedLead.isListedWithRealtor}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600/70">Asking Price</p>
                      <p className="font-bold text-green-600 text-base sm:text-lg">${selectedLead.askingPrice}</p>
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
                  <h3 className="text-xs sm:text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Location Details</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Street Address</p>
                      <p className="font-semibold text-sm sm:text-base break-words">
                        {selectedLead.streetAddress} {selectedLead.address && `(${selectedLead.address})`}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">City</p>
                        <p className="font-semibold text-sm sm:text-base">{selectedLead.city}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">State</p>
                        <p className="font-semibold text-sm sm:text-base">{selectedLead.state}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Country</p>
                        <p className="font-semibold text-sm sm:text-base">{selectedLead.country}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Postal Code</p>
                        <p className="font-semibold text-sm sm:text-base">{selectedLead.postalCode}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions - Mobile Friendly */}
                <div className="flex flex-col sm:flex-row gap-2 pt-4">
                  <a 
                    href={`mailto:${selectedLead.email}`}
                    className="flex-1 bg-propwhiz-blue text-white px-4 py-3 rounded-xl font-semibold hover:bg-blue-700 transition text-center text-sm sm:text-base flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </a>
                  <a 
                    href={`tel:${selectedLead.phone}`}
                    className="flex-1 bg-green-600 text-white px-4 py-3 rounded-xl font-semibold hover:bg-green-700 transition text-center text-sm sm:text-base flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminPage;